/**
 * The bell's two chimes, synthesized with WebAudio — no audio file to ship,
 * cache or fail to load.
 *
 * - `urgent`: a warm marimba-like two-note figure (E5 → B5, a fifth up),
 *   ~0.65 s. Each note is a sine with a short, quiet partial 4× above — the
 *   overtone that gives a struck wooden bar its colour — through a gentle
 *   low-pass, so it cuts through a salon's hair dryers without being shrill.
 * - `soft`: one soft "drop": a sine gliding up a sixth with a quick decay.
 *
 * Browsers keep audio locked until the page got a user gesture (autoplay
 * policy). A context created after the person already clicked somewhere in
 * this page (e.g. the login button) starts right away; after a hard reload it
 * does not. `play` then tries a resume for a moment and otherwise reports
 * `blocked` — the bell shows „Ton aktivieren“ and `installGestureUnlock`
 * resumes on the next click or key press. A blocked chime is dropped, never
 * played late: it would all burst out at once on unlock.
 */

export type ChimeTone = 'urgent' | 'soft';

/** `blocked` = autoplay lock (needs a click); `unsupported` = no WebAudio at all. */
export type ChimeResult = 'played' | 'blocked' | 'unsupported';

export interface ChimePlayer {
  /** Create/resume the context. Call from inside a user gesture. Resolves true once audio can play. */
  unlock: () => Promise<boolean>;
  isUnlocked: () => boolean;
  play: (tone: ChimeTone, volume: number) => Promise<ChimeResult>;
  /** Unlock on the first click / key press / touch anywhere; returns the remover. */
  installGestureUnlock: (onUnlocked?: () => void) => () => void;
  close: () => void;
}

export interface ChimePlayerOptions {
  /** Test seam; defaults to the browser's (webkit)AudioContext, null without WebAudio. */
  createContext?: () => AudioContext | null;
}

/** Start of the last note + its decay, in seconds. */
export const URGENT_CHIME_DURATION_S = 0.65;
const SOFT_CHIME_DURATION_S = 0.38;

/** Headroom: even at 100 % volume the chime peaks well below clipping. */
const MASTER_LEVEL = 0.55;
/** Tames the upper partials — "gut hörbar, nicht schrill". */
const LOWPASS_HZ = 3_400;
const MIN_GAIN = 0.0001;
const START_OFFSET_S = 0.02;
/** How long `play` waits for a suspended context to resume before calling it blocked. */
export const RESUME_WAIT_MS = 250;

const URGENT_NOTES = [
  { freq: 659.25, at: 0, dur: 0.45, level: 0.5 }, // E5
  { freq: 987.77, at: 0.15, dur: 0.5, level: 0.42 }, // B5
] as const;
const MARIMBA_PARTIAL_RATIO = 4;
const MARIMBA_PARTIAL_LEVEL = 0.16;

const GESTURE_EVENTS = ['pointerdown', 'keydown', 'touchend'] as const;

function defaultCreateContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    return new Ctor();
  } catch {
    return null;
  }
}

/** Perceptual volume: a slider at 50 % should sound like half, not like 90 %. */
function volumeToGain(volume: number): number {
  const v = Math.min(1, Math.max(0, Number.isFinite(volume) ? volume : 0));
  return MASTER_LEVEL * v ** 1.6;
}

function envelope(param: AudioParam, t0: number, attack: number, peak: number, end: number): void {
  param.setValueAtTime(MIN_GAIN, t0);
  param.exponentialRampToValueAtTime(Math.max(peak, MIN_GAIN * 2), t0 + attack);
  param.exponentialRampToValueAtTime(MIN_GAIN, end);
}

function tone(
  ctx: AudioContext,
  out: AudioNode,
  { freq, t0, dur, level, attack }: { freq: number; t0: number; dur: number; level: number; attack: number },
): OscillatorNode {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, t0);
  envelope(gain.gain, t0, attack, level, t0 + dur);
  osc.connect(gain);
  gain.connect(out);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
  return osc;
}

function scheduleUrgent(ctx: AudioContext, out: AudioNode, t0: number): void {
  for (const note of URGENT_NOTES) {
    const start = t0 + note.at;
    tone(ctx, out, { freq: note.freq, t0: start, dur: note.dur, level: note.level, attack: 0.008 });
    tone(ctx, out, {
      freq: note.freq * MARIMBA_PARTIAL_RATIO,
      t0: start,
      dur: note.dur * 0.3,
      level: note.level * MARIMBA_PARTIAL_LEVEL,
      attack: 0.004,
    });
  }
}

function scheduleSoft(ctx: AudioContext, out: AudioNode, t0: number): void {
  const osc = tone(ctx, out, { freq: 620, t0, dur: SOFT_CHIME_DURATION_S, level: 0.42, attack: 0.012 });
  // The drop: a quick glide up a sixth right after the attack.
  osc.frequency.exponentialRampToValueAtTime(1_040, t0 + 0.09);
}

export function createChimePlayer(options: ChimePlayerOptions = {}): ChimePlayer {
  const createContext = options.createContext ?? defaultCreateContext;
  let ctx: AudioContext | null = null;
  let unsupported = false;

  const ensureContext = (): AudioContext | null => {
    if (ctx && ctx.state !== 'closed') return ctx;
    if (unsupported) return null;
    ctx = createContext();
    if (!ctx) unsupported = true;
    return ctx;
  };

  const isUnlocked = () => ctx !== null && ctx.state === 'running';

  const unlock = async (): Promise<boolean> => {
    const context = ensureContext();
    if (!context) return false;
    if (context.state !== 'running') {
      try {
        await context.resume();
      } catch {
        return false;
      }
    }
    return (context.state as AudioContextState) === 'running';
  };

  /** Resume, but give up after RESUME_WAIT_MS: without activation Chrome's promise only settles on the next click. */
  const tryResume = async (context: AudioContext): Promise<boolean> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, RESUME_WAIT_MS);
    });
    try {
      await Promise.race([context.resume().catch(() => undefined), timeout]);
    } finally {
      clearTimeout(timer);
    }
    return (context.state as AudioContextState) === 'running';
  };

  const play = async (which: ChimeTone, volume: number): Promise<ChimeResult> => {
    const context = ensureContext();
    if (!context) return 'unsupported';
    // Safari also suspends ("interrupted") an unlocked context after sleep.
    if (context.state !== 'running' && !(await tryResume(context))) return 'blocked';
    const level = volumeToGain(volume);
    if (level <= 0) return 'played';

    const master = context.createGain();
    master.gain.value = level;
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = LOWPASS_HZ;
    master.connect(filter);
    filter.connect(context.destination);

    const t0 = context.currentTime + START_OFFSET_S;
    if (which === 'urgent') scheduleUrgent(context, master, t0);
    else scheduleSoft(context, master, t0);
    return 'played';
  };

  const installGestureUnlock = (onUnlocked?: () => void) => {
    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      for (const type of GESTURE_EVENTS) window.removeEventListener(type, onGesture, true);
    };
    function onGesture() {
      if (isUnlocked()) {
        // Unlocked some other way (e.g. a preview click): still report it.
        remove();
        onUnlocked?.();
        return;
      }
      unlock().then((ok) => {
        if (!ok || removed) return;
        remove();
        onUnlocked?.();
      });
    }
    for (const type of GESTURE_EVENTS) window.addEventListener(type, onGesture, true);
    return remove;
  };

  const close = () => {
    if (ctx && ctx.state !== 'closed') ctx.close().catch(() => undefined);
    ctx = null;
  };

  return { unlock, isUnlocked, play, installGestureUnlock, close };
}
